import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import './Dashboard.css';
import './UploadReceipt.css';

const EMPTY_FORM = { name: '', address: '', vat_id: '' };

function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [query, setQuery] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/api/customers');
      setCustomers(response.data.customers || []);
    } catch (err) {
      setError('Kunden konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const handleEdit = (customer) => {
    setEditingId(customer.id);
    setForm({
      name: customer.name || '',
      address: customer.address || '',
      vat_id: customer.vat_id || '',
    });
    setError('');
    setSuccess('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Bitte Kundenname eingeben.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (editingId) {
        const response = await api.put(`/api/customers/${editingId}`, form);
        setCustomers((current) =>
          current
            .map((customer) => (customer.id === editingId ? response.data.customer : customer))
            .sort((a, b) => a.name.localeCompare(b.name, 'de'))
        );
        setSuccess('Kunde aktualisiert.');
      } else {
        const response = await api.post('/api/customers', form);
        const saved = response.data.customer;
        setCustomers((current) => {
          const without = current.filter((customer) => customer.id !== saved.id);
          return [...without, saved].sort((a, b) => a.name.localeCompare(b.name, 'de'));
        });
        setSuccess(response.status === 200 ? 'Kunde war bereits gespeichert.' : 'Kunde gespeichert.');
      }
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Kunde „${customer.name}“ wirklich löschen?`)) return;
    try {
      await api.delete(`/api/customers/${customer.id}`);
      setCustomers((current) => current.filter((item) => item.id !== customer.id));
      if (editingId === customer.id) resetForm();
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Löschen des Kunden.');
    }
  };

  const filtered = customers.filter((customer) => {
    const haystack = `${customer.name} ${customer.address} ${customer.vat_id}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  return (
    <div>
      <header className="header">
        <div className="header-content">
          <h1>Kunden</h1>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            Zurück zum Dashboard
          </button>
        </div>
      </header>

      <div className="container">
        <div className="card">
          <h2>{editingId ? 'Kunde bearbeiten' : 'Neuen Kunden speichern'}</h2>
          <p className="customer-help">
            Gespeicherte Kunden können beim Erstellen einer Rechnung ausgewählt werden,
            damit Name, Adresse und USt-IdNr nicht jedes Mal neu eingegeben werden müssen.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="label">Firma / Name *</label>
              <input
                type="text"
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Mustermann GmbH"
                required
              />
            </div>
            <div className="form-group">
              <label className="label">Adresse</label>
              <textarea
                className="input"
                rows="3"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Straße, PLZ Ort"
              />
            </div>
            <div className="form-group">
              <label className="label">USt-IdNr</label>
              <input
                type="text"
                className="input"
                value={form.vat_id}
                onChange={(e) => setForm({ ...form, vat_id: e.target.value })}
                placeholder="z.B. DE123456789"
              />
            </div>

            {error && <div className="error" style={{ marginBottom: '15px' }}>{error}</div>}
            {success && <div className="success" style={{ marginBottom: '15px' }}>{success}</div>}

            <div className="form-actions">
              {editingId && (
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Abbrechen
                </button>
              )}
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Speichern...' : editingId ? 'Änderungen speichern' : 'Kunde speichern'}
              </button>
            </div>
          </form>
        </div>

        <div className="dashboard-header">
          <h2>Gespeicherte Kunden</h2>
        </div>

        <div className="card" style={{ padding: '16px 24px' }}>
          <input
            type="search"
            className="input"
            style={{ marginBottom: 0 }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Kunden suchen..."
          />
        </div>

        {loading && <p>Lade Kunden...</p>}

        {!loading && filtered.length === 0 && (
          <div className="card" style={{ textAlign: 'center', color: '#888', padding: '40px' }}>
            <p>{customers.length === 0 ? 'Noch keine Kunden gespeichert.' : 'Keine Kunden gefunden.'}</p>
          </div>
        )}

        <div className="invoices-list">
          {filtered.map((customer) => (
            <div key={customer.id} className="card invoice-card">
              <div className="invoice-header">
                <h3>{customer.name}</h3>
              </div>
              <div className="invoice-details">
                {customer.address && (
                  <p style={{ whiteSpace: 'pre-line' }}><strong>Adresse:</strong> {customer.address}</p>
                )}
                {customer.vat_id && <p><strong>USt-IdNr:</strong> {customer.vat_id}</p>}
                {!customer.address && !customer.vat_id && (
                  <p style={{ color: '#888' }}>Keine weiteren Angaben gespeichert.</p>
                )}
              </div>
              <div className="invoice-actions">
                <button className="btn btn-primary" onClick={() => handleEdit(customer)}>
                  Bearbeiten
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ backgroundColor: '#dc3545' }}
                  onClick={() => handleDelete(customer)}
                >
                  Löschen
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Customers;
